import React, { createContext, useContext, useReducer, useEffect } from 'react';
import apiClient from '../config/axios';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: false,
  error: null
};

// console.log('initialState', initialState);

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
        error: null
      };
    case 'LOGIN_SUCCESS':
      // console.log('LOGIN_SUCCESS', action.payload);
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

/**
 * Se un giorno volessi rivedere il login:
 * quando la pagina carica loading true
 * nel frattempo controllo localStorage per token
 * se c'e' fetcho user e loading false a quel punto renderizzo la route profile
 */
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
 
  useEffect(() => {
    if (state.token) {
      // console.log('SET TOKEN');
      localStorage.setItem('token', state.token);
    } else {
      // console.log('REMOVE TOKEN');
      localStorage.removeItem('token');
    }
  }, [state.token]);

  // Check if user is authenticated on app start
  useEffect(() => {
    const checkAuth = async () => {
      // console.log('checkAuth');
      if (state.token) {
        // console.log('checkAuth', state.token);
        try {
          dispatch({ type: 'SET_LOADING', payload: true });
          const response = await apiClient.get('/api/user/me');
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { user: response.data, token: state.token }
          });
        } catch (error) {
          dispatch({ type: 'LOGOUT' });
        } finally {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
      if (!state.token) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkAuth();
  }, [state.token]);

  const refreshUser = async () => {
    if (state.token) {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const response = await apiClient.get('/api/user/me');
        dispatch({
          type: 'UPDATE_USER',
          payload: response.data
        });
      } catch (error) {
        dispatch({ type: 'LOGOUT' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  };

  const login = async (email, password) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      const response = await apiClient.post('/api/auth/login', { email, password });
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: response.data
      });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Errore durante il login';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      await apiClient.post('/api/auth/register', userData);
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Errore durante la registrazione';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    dispatch({ type: 'LOGOUT' });
  };

  const updateUser = (userData) => {
    dispatch({ type: 'UPDATE_USER', payload: userData });
  };

  const setError = (error) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const setLoading = (loading) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  // Lo chiama OAuthCallbackHandler
  const setToken = (token) => {
    dispatch({ type: 'LOGIN_SUCCESS', payload: { token, user: null } });
  };

  const isDev = process.env.REACT_APP_ENVIRONMENT  === 'development';
  const isProd = process.env.REACT_APP_ENVIRONMENT  === 'production';
  // const isStaging = process.env.REACT_APP_ENVIRONMENT === 'staging';

  const value = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    setError,
    clearError,
    setLoading,
    setToken,
    isDev,
    isProd,
    // isStaging,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 